import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import Renderable from '../../../Component/Renderable';
import { AwaitIterators } from '../AnimationHelper';

type TweenProp = number | number[] | string;

export interface IMainAnimMetaData {
    x?: TweenProp;
    y?: TweenProp;
    alpha?: TweenProp;
    angle?: TweenProp;
    rotation?: TweenProp;

    scaleX?: TweenProp;
    scaleY?: TweenProp;

    duration: number;
    delay?: number;
    easing?: (k: number) => number;
    interpolation?: (v: number[], k: number) => number,

    repeatCount?: number;
    repeatDelay?: number;

    yoyo?: boolean;

    onUpdate?: () => void;
}

const transformProps: Partial<keyof IMainAnimMetaData>[] = [
    'x',
    'y',
    'rotation',
];

const noop = (): void => void 0;

export class MainAnim extends AnimationClip<IMainAnimMetaData> {
    public *execute(
        gameObject: GameObject,
        meta: IMainAnimMetaData
    ): IterableIterator<number> {
        const duration = meta.duration ?? 500;
        const delay = meta.delay ?? 0;
        const easing = meta.easing ?? Easing.Linear.None;
        const repeatCount = meta.repeatCount ?? (meta.yoyo ? 1 : 0);
        const repeatDelay = meta.repeatDelay ?? 0;
        const yoyo = meta.yoyo ?? false;
        let onUpdate = meta.onUpdate ?? noop;
        let iterators = [];

        if ('angle' in meta) {
            if (Array.isArray(meta.angle)) {
                meta.rotation = meta.angle.map((a) => (a / 180) * Math.PI);
            } else if (typeof meta.angle === 'string') {
                const n = Number(meta.angle);
                meta.rotation = `${n > 0 ? '+' : ''}${(n / 180) * Math.PI}`;
            } else {
                meta.rotation = (meta.angle / 180) * Math.PI;
            }
        }

        const items: [object, object][] = [];

        if (transformProps.some(prop => prop in meta)) {
            gameObject.addComponent(TransformComponent);
            const transformComponent = gameObject.getComponent(
                TransformComponent
            );
            const target: any = {};

            for (const prop of transformProps) {
                if (prop in meta) {
                    target[prop] = meta[prop];
                }
            }

            items.push([transformComponent, target]);
        }

        if ('scaleX' in meta || 'scaleY' in meta) {
            gameObject.addComponent(TransformComponent);
            const transformComponent = gameObject.getComponent(
                TransformComponent
            );
            const target: any = {};

            if ('scaleX' in meta) {
                target.x = meta.scaleX;
            }

            if ('scaleY' in meta) {
                target.y = meta.scaleY;
            }

            items.push([transformComponent.scale, target]);
        }

        if ('alpha' in meta) {
            gameObject.addComponent(Renderable);
            const renderable = gameObject.getComponent(Renderable);
            items.push([renderable, { alpha: meta.alpha }]);
        }

        for (const item of items) {
            const [source, target] = item;
            const tween = this.getTween(source);

            tween
                .to(target, duration)
                .delay(delay)
                .easing(easing)
                .repeat(repeatCount)
                .repeatDelay(repeatDelay)
                .yoyo(yoyo)
                .onUpdate(onUpdate);

            if (meta.interpolation) {
                tween.interpolation(meta.interpolation);
            }

            onUpdate = noop;
            iterators.push(this.startTween(tween));
        }

        yield* AwaitIterators(iterators);
    }
}
